/**
 * One `/events` connection, many listeners.
 *
 * The app-wide channel used to be opened twice — once by the tab layout and
 * again by the group screen. socket.io-client does not share a namespace across
 * calls: `lookup()` treats an already-open namespace as a reason to construct a
 * *new* `Manager`, so the second call was a second physical WebSocket, a second
 * JWT handshake, and a second membership of the same `user:{id}` room. Every
 * user-targeted payload was then delivered twice for as long as a group screen
 * stayed mounted.
 *
 * So the connection has exactly one owner, and everything else subscribes here.
 * `subscribe` hands back the removal for the handlers it added — the whole
 * removal, so a screen cannot leak listeners by forgetting one of them, which is
 * the failure a shared socket invites and a solely-owned one hides.
 *
 * No imports on purpose: this is reachable from the root `node --test` suites,
 * which cannot load anything that pulls `react-native`. It is generic over the
 * event map so the payload types can live with the socket that produces them
 * without this file having to import them.
 */

type Handlers<M> = { [K in keyof M]?: (payload: M[K]) => void };

export type EventsBus<M> = {
  /**
   * Listen for app-wide events. Returns the unsubscribe for everything this
   * call registered; a screen effect should return it directly.
   */
  subscribe: (handlers: Handlers<M>) => () => void;
  /** The connection owner forwards each event here. */
  publish: <K extends keyof M>(name: K, payload: M[K]) => void;
  /** Live subscriber count. Exposed so a listener leak is a test failure. */
  size: () => number;
};

export function createEventsBus<M>(): EventsBus<M> {
  const subscribers = new Set<Handlers<M>>();

  return {
    size: () => subscribers.size,

    subscribe: (handlers) => {
      subscribers.add(handlers);
      return () => {
        subscribers.delete(handlers);
      };
    },

    publish: (name, payload) => {
      // Iterate a snapshot, but honour removals made during it: a subscriber
      // added by a handler must not receive the event that added it, and one
      // removed by a handler must not be called after it was removed.
      for (const handlers of [...subscribers]) {
        if (!subscribers.has(handlers)) continue;
        const handler = handlers[name];
        if (!handler) continue;
        try {
          handler(payload);
        } catch {
          // One subscriber's failure is not the others' problem. The tab layout
          // raises notifications and the group screen catches up its thread;
          // neither may be able to silence the other by throwing.
        }
      }
    },
  };
}
